import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { navGroups } from "../navigation";
import { BrandMark } from "./BrandMark";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { brand } from "../config/brand";

interface AppSidebarProps {
    /** Rail width only; the drawer copy is always expanded. */
    collapsed?: boolean;
    onToggleCollapse?: () => void;
    /** Called after a section is picked, so the mobile drawer can close. */
    onNavigate?: () => void;
    /** `drawer` drops the sticky rail chrome and fills its container. */
    variant?: "rail" | "drawer";
}

/**
 * Primary navigation.
 *
 * Sections are grouped under mono eyebrow labels rather than listed flat: eight
 * peers in one column give an admin no sense of where they are, three named
 * groups of two or three do. The active item carries an accent rail on the
 * sidebar's edge, which survives the collapsed state where labels do not.
 */
export const AppSidebar = ({
    collapsed = false,
    onToggleCollapse,
    onNavigate,
    variant = "rail"
}: AppSidebarProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const isDrawer = variant === "drawer";
    const isCollapsed = !isDrawer && collapsed;
    const activeSegment = location.pathname.split("/").filter(Boolean)[0];

    const go = (route: string) => {
        navigate(`/${route}`);
        onNavigate?.();
    };

    // Index across groups so the reveal cascades down the whole rail, not
    // restarting inside each group.
    let order = 0;

    return (
        <aside
            className={`app-sidebar${isDrawer ? " app-sidebar--drawer" : ""}`}
            data-collapsed={isCollapsed}
        >
            <div className="brand">
                <span className="brand__mark">
                    <BrandMark />
                </span>
                <span className="brand__text">
                    <span className="brand__name">{brand.name}</span>
                    <span className="brand__role">{t("app.role")}</span>
                </span>
            </div>

            <nav className="nav" aria-label={t("nav.menu")}>
                {navGroups.map(group => (
                    <div className="nav__group" key={group.labelKey}>
                        <div className="nav__group-label eyebrow">{t(group.labelKey)}</div>
                        {group.items.map(item => {
                            const active = activeSegment === item.route;
                            const label = t(item.labelKey);
                            return (
                                <button
                                    key={item.route}
                                    type="button"
                                    className={`nav__item${active ? " is-active" : ""}`}
                                    style={{ animationDelay: `${28 * order++}ms` }}
                                    aria-current={active ? "page" : undefined}
                                    title={isCollapsed ? label : undefined}
                                    onClick={() => go(item.route)}
                                >
                                    {item.icon}
                                    <span className="nav__item-label">{label}</span>
                                </button>
                            );
                        })}
                    </div>
                ))}
            </nav>

            <div className="sidebar__foot">
                <LanguageSwitcher />
                {onToggleCollapse != null && !isDrawer ? (
                    <button
                        type="button"
                        className="sidebar__collapse"
                        onClick={onToggleCollapse}
                        aria-label={t(isCollapsed ? "nav.expand" : "nav.collapse")}
                        title={t(isCollapsed ? "nav.expand" : "nav.collapse")}
                    >
                        {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </button>
                ) : null}
            </div>
        </aside>
    );
};
